import { AddRounded, CloseRounded, DeleteRounded, InfoRounded, LanguageRounded, SaveRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Autocomplete, Box, CircularProgress, IconButton, MenuItem, Modal, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderEditCellParams, useGridApiRef } from '@mui/x-data-grid'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'
import ApiClient from '../../backend/ApiClient'
import { IApprovalRequest } from '../../definitions/Approval'
import { IFlow } from '../../definitions/Nodes'
import { IFlowChange } from '../../managers/FlowUIManager'
import { CommandStatus } from '../hooks/useCommand'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'
import { FlowEditorMode } from './SettlementFlowEditor'

const FORBIDDEN_KEYS = ['periodStart', 'periodEnd', 'userId', 'contractId', 'type', 'settlementType']

const SETTLEMENT_TYPES = ['Monthly', 'Interim', 'Final']
type SettlementType = 'Monthly' | 'Interim' | 'Final'

type GlobalRow = {
    id: string
    key: string
    type: 'string' | 'number' | 'boolean'
    value: string | number | boolean
}

type P = {
    flow: IFlow
    updateFlow: (change: IFlowChange) => void
    open: boolean
    setOpen: (open: boolean) => void
    statusUpdateFlow: CommandStatus
    approval: IApprovalRequest
    editorMode: FlowEditorMode
}

const noBorderStyle = {
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: 'transparent'
        },
        '&:hover fieldset': {
            borderColor: 'transparent'
        },
        '&.Mui-focused fieldset': {
            borderColor: 'transparent'
        }
    }
}

const TypeEditCell = (params: GridRenderEditCellParams) => {
    const handleChange = e => {
        const newType = e.target.value

        params.api.setEditCellValue({
            id: params.id,
            field: 'type',
            value: newType
        })

        params.api.setEditCellValue({
            id: params.id,
            field: 'value',
            value: newType === 'boolean' ? false : ''
        })
    }

    return (
        <TextField
            select
            size='small'
            fullWidth
            value={params.value}
            onChange={handleChange}
            sx={noBorderStyle}
            InputProps={{ sx: { height: '100%' } }}>
            <MenuItem value='string'>string</MenuItem>
            <MenuItem value='number'>number</MenuItem>
            <MenuItem value='boolean'>boolean</MenuItem>
        </TextField>
    )
}

const ValueEditCell = (params: GridRenderEditCellParams) => {
    const type = params.row.type

    if (type === 'boolean') {
        return (
            <TextField
                select
                sx={noBorderStyle}
                InputProps={{ sx: { height: '100%' } }}
                size='small'
                fullWidth
                value={params.value ? 'true' : 'false'}
                onChange={e =>
                    params.api.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: e.target.value === 'true'
                    })}>
                <MenuItem value='true'>true</MenuItem>
                <MenuItem value='false'>false</MenuItem>
            </TextField>
        )
    }

    return (
        <TextField
            sx={noBorderStyle}
            InputProps={{ sx: { height: '100%' } }}
            size='small'
            fullWidth
            type={type === 'number' ? 'number' : 'text'}
            value={params.value ?? ''}
            onChange={e =>
                params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: type === 'number' ? Number(e.target.value) : e.target.value
                })}
        />
    )
}

const validateRow = (row: GlobalRow, allRows: GlobalRow[]) => {
    const key = row.key.trim()

    const duplicates = allRows.filter(r => r.key.trim() === key)
    const isDuplicate = key !== '' && duplicates.length > 1

    return {
        isKeyEmpty: key === '',
        isValueEmpty: row.value === '' || row.value === null || row.value === undefined,
        isForbidden: FORBIDDEN_KEYS.includes(key),
        isDuplicate
    }
}

const SetNodeGlobalsModal = ({ flow, updateFlow, open, setOpen, statusUpdateFlow, approval, editorMode }: P) => {
    const [periodStart, setPeriodStart] = useState(flow.globals.periodStart ?? '')
    const [periodEnd, setPeriodEnd] = useState(flow.globals.periodEnd ?? '')
    const [contractId, setContractId] = useState<number | null>(flow.globals.contractId ?? null)
    const [settlementType, setsettlementType] = useState<SettlementType>(flow.globals?.settlementType ?? 'Monthly')
    const apiRef = useGridApiRef()

    const [rows, setRows] = useState<GlobalRow[]>([])

    const { status: contractStatus, response: contractResponse } = useQuery({
        queryKey: buildQueryKey('SetNodeGlobalsModal-contract'),
        queryFn: () => ApiClient.getHeadContractNames(),
        cache: false
    })

    useEffect(() => {
        if (!open) return

        const newRows: GlobalRow[] = Object.entries(flow.globals)
            .filter(([k]) => !FORBIDDEN_KEYS.includes(k))
            .map(([key, value]) => {
                const type = typeof value === 'boolean'
                    ? 'boolean'
                    : typeof value === 'number'
                        ? 'number'
                        : 'string'

                return {
                    id: uuid(),
                    key,
                    type,
                    value
                }
            })

        setRows(newRows)
    }, [open, flow.globals])

    const selectedContract = contractResponse?.find(c => c.id === contractId) ?? null

    const loading = contractStatus === 'loading' || contractStatus === 'stale'

    const canEdit = approval.status !== 'approved' && editorMode === 'edit'

    const editableGlobals = editorMode !== 'audit' && editorMode !== 'compare'

    const hasInvalidRows = rows.some(r => {
        const v = validateRow(r, rows)
        return v.isKeyEmpty || v.isValueEmpty || v.isForbidden || v.isDuplicate
    })

    const isSaveDisabled = !canEdit || hasInvalidRows

    const addRow = useCallback(() => {
        setRows(prev => {
            const newRow = { id: uuid(), key: '', type: 'string' as const, value: '' }
            const updated = [...prev, newRow]

            setTimeout(() => {
                const index = updated.length - 1
                apiRef.current?.scrollToIndexes({ rowIndex: index })
            }, 0)

            return updated
        })
    }, [apiRef])

    const deleteRow = useCallback((id: string) => {
        setRows(prev => prev.filter(r => r.id !== id))
    }, [])

    const processRowUpdate = useCallback((newRow: GlobalRow, oldRow: GlobalRow) => {
        setRows(prev => prev.map(r => r.id === oldRow.id ? newRow : r))
        return newRow
    }, [])

    const columns: GridColDef[] = useMemo(() => ([
        {
            field: 'key',
            headerName: 'Key',
            flex: 1,
            editable: canEdit
        },
        {
            field: 'type',
            headerName: 'Type',
            flex: 0.7,
            editable: canEdit,
            renderEditCell: params => <TypeEditCell {...params} />
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 1,
            editable: canEdit,
            renderEditCell: params => <ValueEditCell {...params} />,
            renderCell: params => (
                <Tooltip
                    title={params.row.value === '' && 'Value must be filled'}
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: theme => theme.palette.error.main
                            }
                        },
                        arrow: {
                            sx: {
                                color: theme => theme.palette.error.main
                            }
                        }
                    }}>
                    <Box style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <ValueEditCell {...params} />
                    </Box>

                </Tooltip>
            )
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Action',
            flex: 0.3,
            getActions: params => [
                <GridActionsCellItem
                    key={params.id}
                    icon={<DeleteRounded color={canEdit ? 'error' : undefined} />}
                    label='Delete'
                    disabled={!canEdit}
                    onClick={() => deleteRow(params.id as string)}
                />
            ]
        }
    ]), [rows, canEdit])

    const getCellClassName = params => {
        const v = validateRow(params.row, rows)

        if (params.field === 'key' && (v.isKeyEmpty || v.isForbidden || v.isDuplicate)) {
            return 'error-cell'
        }
        if (params.field === 'value' && v.isValueEmpty) {
            return 'error-cell'
        }
        return ''
    }

    const onSave = () => {
        if (!canEdit) return

        const newGlobals: Record<string, any> = {}

        for (const r of rows) {
            if (!r.key.trim()) continue
            newGlobals[r.key] = r.value
        }

        updateFlow({
            flow: {
                ...flow,
                globals: {
                    ...newGlobals,
                    contractId,
                    periodStart,
                    periodEnd,
                    settlementType
                }
            },
            needsSync: true,
            isUpdated: true
        })
    }

    useEffect(() => {
        if (statusUpdateFlow === 'success')
            snackbar.show({ severity: 'success', text: 'Globals saved correctly' })

        if (statusUpdateFlow === 'error')
            snackbar.show({
                severity: 'error',
                text: 'An error occurred while saving globals'
            })
    }, [statusUpdateFlow])

    return (
        <Modal
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                left: 'var(--left-nav-width)'
            }}
            open={open}
            onClose={() => setOpen(false)}>
            <Paper
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    width: '48vw',
                    height: '75vh'
                }}>
                <Stack spacing={3} flex={1} overflow='hidden'>
                    <Stack flexDirection='row' alignItems='center' justifyContent='space-between'>
                        <Stack flexDirection='row' gap={1.5} alignItems='center'>
                            <LanguageRounded fontSize='large' />
                            <Typography variant='h6'>Globals</Typography>
                        </Stack>
                        <IconButton onClick={() => setOpen(false)}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                    <Stack flexDirection='row' alignItems='center' gap={2} width='100%'>
                        <Autocomplete
                            loading={loading}
                            size='small'
                            disabled={!editableGlobals}
                            sx={{ width: '40%' }}
                            value={selectedContract}
                            onChange={(_, v) => setContractId(v?.id ?? null)}
                            options={contractResponse ?? []}
                            getOptionLabel={option => option?.name ?? ''}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label='Contract'
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress color='inherit' size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                        <TextField
                            select
                            sx={{ width: '18%' }}
                            type='date'
                            size='small'
                            value={settlementType}
                            disabled={!editableGlobals}
                            onChange={e => setsettlementType(e.target.value as SettlementType)}
                            InputLabelProps={{ shrink: true }}
                            label='From'>
                            {SETTLEMENT_TYPES.map((v, i) => (
                                <MenuItem key={i} value={v}>{v}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            sx={{ width: '21%' }}
                            type='date'
                            size='small'
                            value={periodStart}
                            disabled={!editableGlobals}
                            onChange={e => setPeriodStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            label='From'
                        />
                        <Typography>-</Typography>
                        <TextField
                            sx={{ width: '21%' }}
                            type='date'
                            size='small'
                            value={periodEnd}
                            disabled={!editableGlobals}
                            onChange={e => setPeriodEnd(e.target.value)}
                            label='To'
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                    <Stack spacing={2} flex={1} overflow='hidden'>
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            <Tooltip
                                title='Insert the global variables that you want to utilize in the flow'
                                arrow>
                                <InfoRounded fontSize='small' />
                            </Tooltip>
                            <Typography variant='subtitle2' fontWeight='bold'>
                                Global variables
                            </Typography>
                        </Stack>
                        <Stack flex={1} overflow='hidden'>
                            <DataGrid
                                apiRef={apiRef}
                                getCellClassName={getCellClassName}
                                rows={rows}
                                columns={columns}
                                editMode='row'
                                isCellEditable={() => canEdit}
                                processRowUpdate={processRowUpdate}
                                onProcessRowUpdateError={err => console.error(err)}
                                disableRowSelectionOnClick
                                hideFooter
                                sx={{
                                    'opacity': canEdit ? 1 : 0.6,
                                    '& .error-cell': {
                                        border: theme => `1px solid ${theme.palette.error.main}`
                                    }
                                }}
                            />
                            <Stack width='100%'>
                                <IconButton
                                    color='primary'
                                    size='small'
                                    disabled={!canEdit}
                                    onClick={addRow}
                                    sx={{ borderRadius: 1 }}>
                                    <AddRounded />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack flexDirection='row' sx={{ alignSelf: 'end' }}>
                        <LoadingButton
                            loading={statusUpdateFlow === 'loading'}
                            disabled={isSaveDisabled}
                            loadingPosition='start'
                            size='small'
                            variant='contained'
                            startIcon={<SaveRounded fontSize='small' />}
                            onClick={onSave}>
                            save globals
                        </LoadingButton>
                    </Stack>
                </Stack>
            </Paper>
        </Modal>
    )
}

export default memo(SetNodeGlobalsModal)
